import * as THREE from 'three';
import { VERTICAL_ENGAGEMENT_RANGE } from './spatial';

export type AimPreset = 'lockon' | 'cone' | 'forward';
export const AIM_PRESETS: AimPreset[] = ['lockon', 'cone', 'forward'];
export const AIM_PRESET_LABELS: Record<AimPreset, string> = {
  lockon: 'LOCK-ON',
  cone: 'ASSIST',
  forward: 'FREE AIM',
};

// INVARIANT (T2 review, three lenses converged on it): AIM_RANGE must be >=
// the largest aggroRange in ai/archetypes.ts (S10: rat_king's 18, unchanged
// from the elite_rat stand-in it replaced). With the T2
// hold-fire rule, anything allowed to CHASE the player from farther than the
// gun engages produces a dead band where an enemy is closing in while the
// gun stays silent — which reads as "my gun is broken," the exact feeling
// hold-fire exists to remove. Re-check this pairing whenever either number
// is tuned.
const AIM_RANGE = 18;
const CONE_HALF_ANGLE = Math.PI / 3; // 60° either side of facing = a 120° forward cone

// S08 memory audit: the target-selection loop below allocated a fresh
// Vector3 EVERY candidate, every tick (main.ts calls this once/tick) — at
// §3's ≤24-enemy ceiling that's up to 1440+ allocations/sec. Module-level
// (not per-call) since the function is a plain synchronous export with no
// persistent instance to hang state off; safe because JS is single-threaded
// and nothing here awaits, so no call can interleave with another's use of
// this scratch vector. Purely an intermediate — read (`.length()`/
// `.angleTo()`) and discarded within the SAME loop iteration, never stored
// or returned, so reuse can't leak stale state across iterations or calls.
const _scratchToTarget = new THREE.Vector3();

/** Structural interface, not `ai/enemy.ts`'s `Enemy` directly — keeps `combat/` free of an `ai/` import. */
export interface AimTarget {
  position: THREE.Vector3;
  isTargetable: boolean;
}

/** What the aim resolver hands back: where to shoot, and whether shooting makes sense at all right now. */
export interface AimResolution {
  dir: THREE.Vector3;
  /**
   * True when at least one targetable enemy passes EVERY range gate
   * (targetable + vertical engagement window + within AIM_RANGE) —
   * regardless of preset, including 'forward' (computed honestly so a
   * future consumer, e.g. an "in combat" HUD cue, can't be misled by a
   * preset-dependent flag). T2's hold-fire rule reads this: Karan's second
   * playtest — "I want the gun turned off unless there is an enemy nearby;
   * once the enemy is dead it doesn't make sense to keep shooting." The
   * FIRING POLICY itself lives with the caller (main.ts): assisted presets
   * (lockon/cone) fire only while this is true; 'forward' (FREE AIM) is
   * the deliberate always-fire option he asked to keep available.
   */
  enemyInRange: boolean;
  /**
   * Horizontal distance to the resolved `best` target, or `null` when
   * nothing was actually selected (no qualifying target at all, 'forward'
   * never selects one, or 'cone' fell back to facing-forward because
   * nothing qualified inside the cone). AIM_RANGE (18) is a single shared
   * "is anything worth engaging nearby" gate across every weapon — it is
   * NOT a per-weapon reach guarantee (S04 review finding: Stickball Bat's
   * real range is 2m, Sawed-Off's is 7m, both well under 18). The caller
   * (main.ts) gates actually FIRING a specific weapon on
   * `targetDistance !== null && targetDistance <= weapon.range` in
   * addition to `enemyInRange`, so a short-range weapon doesn't burn its
   * cooldown swinging/firing at something it can't possibly reach yet.
   */
  targetDistance: number | null;
}

/**
 * Track X's real lesson (PLAN-CITY §1.7): manual aim decoupled from
 * movement was the prototype's core complaint. These 3 presets are a
 * spectrum from full assist to none, so T1-T3 tuning can find where "fun"
 * actually sits without re-deriving the lesson from scratch:
 *  - lockon: aims at the nearest enemy in range, any direction.
 *  - cone: aims at the nearest enemy within a forward-facing cone; with
 *    enemies nearby but none in the cone, it aims straight ahead
 *    (suppressive fire toward a fight that's actually happening).
 *  - forward: never targets anything — aims wherever the player is
 *    facing. Doubles as FREE AIM, the manual always-fire mode.
 * Since T2, whether the gun actually FIRES is a separate question from
 * where it aims — see AimResolution.enemyInRange above.
 */
export function resolveAimDirection(preset: AimPreset, playerPos: THREE.Vector3, facing: number, targets: AimTarget[]): AimResolution {
  const forward = new THREE.Vector3(Math.sin(facing), 0, Math.cos(facing));

  let best: AimTarget | null = null;
  let bestDist = Infinity;
  let enemyInRange = false;
  for (const t of targets) {
    if (!t.isTargetable) continue;
    // A target on a different floor level (the fire-escape ramp/rooftop
    // sitting directly above ground-level enemies, e.g.) is not a sane
    // lock even when it's horizontally close — S02's own review caught
    // this reachable through S01's own headline traversal feature. It
    // also deliberately keeps enemyInRange FALSE for cross-floor enemies,
    // so the T2 hold-fire rule doesn't leave the gun blazing at a floor.
    if (Math.abs(t.position.y - playerPos.y) > VERTICAL_ENGAGEMENT_RANGE) continue;
    const toTarget = _scratchToTarget.subVectors(t.position, playerPos).setY(0);
    const dist = toTarget.length();
    // Written as a NEGATED CONJUNCTION on purpose: a NaN dist (a poisoned
    // position on either side) fails `dist >= 1e-4 && dist <= AIM_RANGE`
    // and is skipped — the gate fails CLOSED. The naive form
    // (`dist > AIM_RANGE || dist < 1e-4`) let NaN fall through both
    // comparisons and latch enemyInRange permanently TRUE (T2 review; NaN
    // poisoning has bitten this project before, see main.ts's __BR3D__
    // notes). The 1e-4 floor (an enemy exactly on the player's XZ) also
    // gates FIRING now, not just aim — unreachable in play (attack ranges +
    // separation keep real enemies ~1.3m out), reachable only via
    // teleport-style position writes from the debug hook.
    if (!(dist >= 1e-4 && dist <= AIM_RANGE)) continue;
    enemyInRange = true; // passed every range gate — set BEFORE the cone filter on purpose (see AimResolution)
    if (preset === 'forward') continue; // free aim never targets; the flag above is still computed honestly
    if (preset === 'cone' && toTarget.angleTo(forward) > CONE_HALF_ANGLE) continue;
    if (dist < bestDist) {
      bestDist = dist;
      best = t;
    }
  }
  if (!best) return { dir: forward, enemyInRange, targetDistance: null };
  return { dir: new THREE.Vector3().subVectors(best.position, playerPos).setY(0).normalize(), enemyInRange, targetDistance: bestDist };
}
